import { Menu } from '@highlight/ui'
import { AttachmentMenuItem, AttachmentMenuItemType, AttachmentSubmenu } from './attachments-menu'
import { useEffect, useMemo } from 'react'
import { logAmplitudeEvent } from '../../../../../utils'
import { useBrowserTabs } from '../../../../../hooks/browser-tabs'
import { useCurrentWindowToSelection, useWindowSelection, WindowSelectionType } from '../../../../../store/atoms'
import { useAddWindow } from '../../../../../hooks/useAddWindow'
import { useUniqueWindowSources } from '../../../../../hooks/useUniqueWindowSources'

export const BrowserTabsMenuItem = (item: AttachmentMenuItemType) => {
  const { data, refetch } = useBrowserTabs()

  useEffect(() => {
    refetch()
  }, [])

  const windowSources = useUniqueWindowSources()

  const { removeSelectedWindow, isWindowSelected } = useWindowSelection()
  const currentWindow = useCurrentWindowToSelection()
  const onAddWindow = useAddWindow()

  const areAnyTabsAvailable = useMemo(() => {
    if (!data || data.error) return false
    if (data.active && data.active.length > 0) return true
    if (data.windows && data.windows.length > 0) {
      return data.windows.some((window: any) => window.length > 0)
    }
  }, [data])

  const [activeTabs, excludeFromWindows] = useMemo(() => {
    if (!data || !data?.active) return [[], []]
    const active = data.active

    let excludeFromWindows: number[] = []

    const result = active
      .map((tab: any) => {
        // TODO this could glitch if one of your open tabs is called "Slack" or something. would be easy to get around if a user encounters it though
        const matchingWindow = windowSources.find((w) => w.window?.windowTitle === tab.title)
        if (matchingWindow) {
          excludeFromWindows.push(tab.id)
          return matchingWindow
        }
      })
      .filter(Boolean) as AttachmentMenuItemType[]

    return [result, excludeFromWindows]
  }, [data?.active, windowSources])

  const windows = useMemo(() => {
    if (!data || !data?.windows) return []
    return data.windows.map((window: any) => {
      return window.filter((tab: any) => {
        return !excludeFromWindows.includes(tab.id)
      })
    })
  }, [data?.windows, excludeFromWindows])

  return (
    <AttachmentSubmenu item={item}>
      {areAnyTabsAvailable &&
        activeTabs.map((tab: any) => {
          return (
            <AttachmentMenuItem
              {...tab}
              key={tab.window.id}
              onClick={(e) => {
                e.stopPropagation()
                if (tab.selected) {
                  removeSelectedWindow(tab.window!)
                  logAmplitudeEvent('Remove Window Context', {
                    appName: tab.window!.appName,
                    focusedWindow:
                      tab.window!.appName === currentWindow?.appName &&
                      tab.window!.pid === currentWindow?.pid &&
                      tab.window!.windowTitle === currentWindow?.windowTitle,
                    trigger: 'Attachments Menu',
                  })
                  return
                }
                onAddWindow(tab.window!)
                logAmplitudeEvent('Add Window Context', {
                  appName: tab.window!.appName,
                  focusedWindow:
                    tab.window!.appName === currentWindow?.appName &&
                    tab.window!.pid === currentWindow?.pid &&
                    tab.window!.windowTitle === currentWindow?.windowTitle,
                  trigger: 'Attachments Menu',
                })
              }}
            />
          )
        })}
      {areAnyTabsAvailable &&
        data?.windows &&
        windows.map((window: any, idx: number) => {
          return (
            <div key={idx}>
              {(idx !== 0 || activeTabs?.length > 0) && <Menu.Separator />}
              {window.map((tab: any) => {
                const tabAsWindow: WindowSelectionType = {
                  appName: 'Tab',
                  appIcon: tab.base64Favicon,
                  pid: -1,
                  windowTitle: tab.title,
                  tabId: tab.id,
                }

                return (
                  <AttachmentMenuItem
                    selectable={true}
                    key={tab.id}
                    selected={isWindowSelected(tabAsWindow)}
                    onClick={() => {
                      if (isWindowSelected(tabAsWindow)) {
                        removeSelectedWindow(tabAsWindow)
                      } else {
                        onAddWindow(tabAsWindow)
                      }
                    }}
                    id={tab.id}
                    label={tab.title}
                    icon={<img src={tab.base64Favicon} alt={tab.title} className={'h-4 w-4 rounded-full'} />}
                  />
                )
              })}
            </div>
          )
        })}
      {!areAnyTabsAvailable && (
        <AttachmentMenuItem
          id={'not-available'}
          label={<span className={'leading-normal'}>{'No tabs available'}</span>}
          disabled
          onClick={() => {}}
        />
      )}
    </AttachmentSubmenu>
  )
}
